// Util
import { hasVisibleDescendants } from '../elements/utils/elementTypeUtils';
import { getElementId, getLocationParentId, getLocationSection } from '../elements/utils/elementPropertyUtils';
import { sortBy2dPosition, sortByScoreProperty } from '../elements/utils/elementSortUtils';
import {
    formatCommentAsIdeaObject,
    canFormatElementAsIdeaObject,
    formatElementAsIdeaObject,
} from './aiResponseFormattingUtils';

// Types
import { BoardSections } from '../boards/boardConstants';
import { MNElement } from '../elements/elementModelTypes';
import { AiIdea } from './aiConversationTypes';
import { convertIdeaToStructuredText } from './aiIdeaConversionUtils';
import { MNComment } from '../comments/commentTypes';

export const getBoardIdeaContent = (
    board: MNElement,
    visibleDescendants: MNElement[],
    visibleDescendantComments: MNComment[],
    // for tests, we need the ids so we can ensure the order of the ideas is correct,
    // but usually we don't want them because they use a lot of tokens unnecessarily
    includeIds = false,
): AiIdea[] => {
    const boardContent = formatElementAsIdeaObject(board, includeIds);
    // if the board can't be formatted as an idea object, bail out
    // (this is mostly a type guard, don't think it will ever happen)
    if (!boardContent) return [];

    const boardId = getElementId(board);

    // only include elements that can be formatted as idea objects
    const validDescendants = visibleDescendants.filter(canFormatElementAsIdeaObject);

    const directDescendants = validDescendants.filter((element) => {
        return getLocationParentId(element) === boardId;
    });

    // separately sort canvas and inbox elements
    const canvasDescendants = directDescendants
        .filter((element) => getLocationSection(element) === BoardSections.CANVAS)
        .sort(sortBy2dPosition);

    const inboxDescendants = directDescendants
        .filter((element) => getLocationSection(element) === BoardSections.INBOX)
        .sort(sortByScoreProperty);

    const sortedDescendants = [...canvasDescendants, ...inboxDescendants];

    // rehydrate the column/task list children into the flattened list
    const descendantsWithRehydratedChildren = sortedDescendants.flatMap((element) => {
        if (!hasVisibleDescendants(element)) return [element];

        const containerId = getElementId(element);

        const columnDescendants = validDescendants
            .filter((element) => {
                return getLocationParentId(element) === containerId;
            })
            .sort(sortByScoreProperty);

        return [element, ...columnDescendants];
    });

    const commentsContent: AiIdea[] = visibleDescendantComments
        .map((comment) => formatCommentAsIdeaObject(comment))
        .filter((idea) => idea?.text?.length) as AiIdea[];

    // convert the elements into "idea" objects
    const content: AiIdea[] = descendantsWithRehydratedChildren
        .map((element) => formatElementAsIdeaObject(element, includeIds))
        .filter((idea) => idea?.text?.length || idea?.hex?.length) as AiIdea[]; // filter out undefined ideas; // filter out empty ideas

    // we are putting the comments in the end as they are not as important
    // as the other elements in precedence for the prompts
    return [boardContent, ...content, ...commentsContent];
};

// since the actual tokenizer is a very heavy dependency (1.4mb regex!),
// we'll just estimate the token length on client
// https://help.openai.com/en/articles/4936856-what-are-tokens-and-how-to-count-them
export const getEstimatedTokenLength = (text: string): number =>
    Math.ceil(text.length / 4) + // 1 token per 4 characters
    3; // 3 for \n###\n idea separators

export const getLimitedIdeaContent = (content: AiIdea[], maxTokenLength: number): AiIdea[] => {
    const [limitedIdeas] = content.reduce(
        ([limitedContent, estimatedLength], idea) => {
            const structuredText = convertIdeaToStructuredText(idea);
            const estimatedTokens = getEstimatedTokenLength(structuredText);
            const newEstimatedLength = estimatedLength + estimatedTokens;

            if (newEstimatedLength > maxTokenLength) return [limitedContent, estimatedLength];

            return [[...limitedContent, idea], newEstimatedLength];
        },
        [[] as AiIdea[], 0] as [AiIdea[], number],
    );

    return limitedIdeas;
};
